// Requests that get no answer, and saves sent twice (brief 1E-27). Pure: the screen's one request
// helper (app/ui/dom.ts api) and the server's one request key helper (server/core/request-keys.ts)
// both read these.

/** The header a save carries its request key in. */
export const REQUEST_KEY_HEADER = "x-request-key";

/** What a screen shows when a read or a save got no usable answer. */
export const COULD_NOT_LOAD = "Couldn't load. Try again.";
export const COULD_NOT_SAVE = "Couldn't save. Try again.";
/** The one line shown while a save whose answer was lost is sent again. */
export const SAVING_AGAIN = "Connection lost. Saving again.";

/**
 * How long the screen waits for an answer before it gives up on that try. The server answers or
 * fails within about 25 seconds (server/core/db.ts DB_LIMITS) and Netlify ends a request at 30.
 */
export const WAIT_MS = { read: 20_000, write: 28_000 };

/** A request key: letters, digits, - and _, 16 to 80 long (a random UUID is one). */
export const isRequestKey = (key: unknown): key is string =>
  typeof key === "string" && /^[A-Za-z0-9_-]{16,80}$/.test(key);

/** How long a resend waits for the first try of the same key, still being saved, to answer. */
export const KEY_WAIT_MS = 20_000;

/**
 * Whether a try is sent once more: no answer at all (lost, or no answer in time), or an answer
 * that came from between the screen and the app rather than from the app (502, 503, 504, or a 500
 * that isn't the app's JSON). Anything the app itself answered, 4xx included, is final.
 */
export function shouldRetry(answer: { status: number; json: boolean }): boolean {
  if (answer.status === 0) return true;
  if (answer.status === 502 || answer.status === 503 || answer.status === 504) return true;
  return answer.status === 500 && !answer.json;
}

/** The methods that change something: each carries a request key from the screen. */
export const isWrite = (method: string) => method !== "GET" && method !== "HEAD";

/**
 * What the server does with a save's request key, given the row already kept for it (or none):
 * run it (first time, or after a try that failed on the server), send back the kept answer, wait
 * for the try still being saved, or refuse a key reused for a different save.
 */
export function keyDecision(
  kept: {
    method: string;
    path: string;
    status: number | null;
    released: boolean;
  } | null,
  sent: { method: string; path: string },
): "run" | "replay" | "wait" | "mismatch" {
  if (!kept) return "run";
  if (kept.method !== sent.method || kept.path !== sent.path) return "mismatch";
  if (kept.status !== null) return "replay";
  if (kept.released) return "run";
  return "wait";
}
